import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import { Dialog, DialogBackdrop, DialogProps } from 'reakit/Dialog';
import { Transition, AnimatePresence, motion } from 'framer-motion';
import { CKDialogSize } from '../../common/types';

interface CKModalComposition {
  Header: React.FC<CKModalSubComponentProps>;
  Heading: React.FC<CKModalSubComponentProps>;
  Body: React.FC<CKModalSubComponentProps>;
  Footer: React.FC<CKModalSubComponentProps>;
}

export interface CKModalProps extends DialogProps {
  transition?: Transition;
  size?: CKDialogSize;
}

export const CKModal: FunctionComponent<CKModalProps> & CKModalComposition = ({
  transition = { ease: 'circOut', duration: 0.2 },
  children,
  size = CKDialogSize.xs,
  ...rest
}) => {
  const modalClass = classNames(`ck-modal`, `ck-modal--${size}`);

  return (
    <React.Fragment>
      <DialogBackdrop {...rest}>
        {props => (
          <AnimatePresence>
            {props.hidden ? null : (
              <motion.div
                key="backdrop"
                className="ck-dialog-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              ></motion.div>
            )}
          </AnimatePresence>
        )}
      </DialogBackdrop>
      <Dialog {...rest}>
        {props => (
          <AnimatePresence>
            {props.hidden ? null : (
              <motion.div
                key="dialog"
                className="ck-dialog-container"
                transition={transition}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div {...props} className={modalClass}>
                  {children}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </Dialog>
    </React.Fragment>
  );
};

export interface CKModalSubComponentProps {
  className?: string;
}

CKModal.Header = ({ children, className = '' }) => (
  <div className={`ck-modal-header ${className}`}>{children}</div>
);

CKModal.Heading = ({ children, className = '' }) => (
  <h1 className={`ck-modal--heading ${className}`}>{children}</h1>
);

CKModal.Body = ({ children, className = '' }) => (
  <div className={`ck-modal-body ${className}`}>{children}</div>
);

CKModal.Footer = ({ children, className = '' }) => (
  <div className={`ck-modal-footer ${className}`}>{children}</div>
);
